import { Component, computed, effect, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { PermissionService } from '@shared/services/permission/permission.service';
import { RouterModule, Router } from '@angular/router';
import { AuthService } from '@shared/services/auth.service';
import { DropdownComponent } from '../../ui/dropdown/dropdown.component';
import { DropdownItemTwoComponent } from '../../ui/dropdown/dropdown-item/dropdown-item.component-two';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-user-dropdown',
  standalone: true,
  templateUrl: './user-dropdown.component.html',
    imports: [
    CommonModule,
    RouterModule,
    DropdownComponent,
    DropdownItemTwoComponent,
    TranslocoModule
  ]
})
export class UserDropdownComponent {
  isOpen = false;
  perfilImageSrc: string | null = null;

  // Computed reactive para datos del perfil usando la signal directamente
  firstnameUSer = computed(() => this.usuarioService.perfil()?.firstname || 'Usuario');
  lastnameUSer = computed(() => this.usuarioService.perfil()?.lastname || 'Lastname');
  /** Correo del usuario; vacío si no hay (la plantilla muestra `common.noEmail`). */
  emailUser = computed(() => this.usuarioService.perfil()?.username || '');
  profileImageUrl = computed(() => this.usuarioService.perfil()?.profileImageUrl || 'assets/images/Partner-Core-No-Image.webp');

  /**
   * URL del módulo Perfil según el ámbito del usuario. Existen dos módulos "Perfil" en el
   * catálogo — `/admin-sistema/perfil` (grupo Sistema, personal del Provider) y `/admin/perfil`
   * (Control General, empresas) — y el `permissionGuard` valida la URL solicitada contra los
   * módulos del sidebar del rol. Hardcodear `/admin/perfil` rompía para un ADMIN del Provider
   * (su Perfil real es `/admin-sistema/perfil`), que caía en 404 unauthorized. Se resuelve el
   * módulo `/perfil` sobre el que el usuario tiene canView; con fallback por companyType.
   */
  readonly profileUrl = computed(() => {
    const perfil = this.usuarioService.perfil();
    const roleId = perfil?.role?.id;

    if (roleId) {
      const sidebar = this.permissionService.sidebarCache().get(roleId);
      if (sidebar) {
        for (const group of sidebar.groups) {
          for (const category of group.categories) {
            for (const module of category.modules) {
              if (module.url && module.canView &&
                  module.url.toLowerCase().replace(/\/$/, '').endsWith('/perfil')) {
                return module.url;
              }
            }
          }
        }
      }
    }

    // Fallback por ámbito mientras el sidebar aún no está en caché.
    return perfil?.companyType === 'PROVIDER' ? '/admin-sistema/perfil' : '/admin/perfil';
  });

  // Effect para cargar imagen cuando el perfil cambie
  perfilEffect = effect(() => {
    const perfil = this.usuarioService.perfil();
    if (!perfil) return;
    
    this.cargarImagenPerfil();
  });

  constructor(
    private authService: AuthService,
    private usuarioService: UsuarioService,
    private permissionService: PermissionService,
    private router: Router,
    private cd: ChangeDetectorRef
  ) {
    this.usuarioService.getPerfil().subscribe(perfil => {
      // Asegura el sidebar en caché para que profileUrl() resuelva el módulo Perfil correcto.
      const roleId = perfil?.role?.id;
      if (roleId) this.permissionService.getSidebarForRoleCached(roleId).subscribe();
    });
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  closeDropdown() {
    this.isOpen = false;
  }

  logout(): void {
    this.authService.logout('manual');
    this.router.navigate(['/auth/login']);
  }

  cargarImagenPerfil(): void {
    const profileUrl = this.usuarioService.perfil()?.profileImageUrl;
    if (this.perfilImageSrc?.startsWith('blob:')) URL.revokeObjectURL(this.perfilImageSrc);
    // profileImageUrl es una URL pública directa (object storage); usable en <img src>.
    const isUrl = !!profileUrl && /^https?:\/\//i.test(profileUrl);
    this.perfilImageSrc = isUrl ? profileUrl! : 'assets/images/Partner-Core-No-Image.webp';
    this.cd.markForCheck();
  }
}